import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNotification } from "../../../utils/NotificationsContext";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowBackIosNew as ArrowBackIcon,
  Close as CloseIcon,
  EmailOutlined as EmailOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { userStore } from "../../../stores/sharedStores";
import { useStores as useSwageazyStores } from "../../../projects/swageazy/use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { NewUser } from "../../../models/User";
import ActivityLogDrawer from "../../components/common/drawer/ActivityLogDrawer";
import UserAlertSettingsCard from "../../components/users/UserAlertSettingsCard";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import { getModernPageColors } from "../../../styles/modernListingPageSx";
import {
  blockEdgeSpaces,
  inputFromKeyEvent,
  isEdgeSpaceKey,
} from "../../../utils/blockEdgeSpaces";

interface AddUserProps {
  customer?: FilterValue[];
  setLoadingForPopup?: (loading: boolean) => void;
  isNavigationNeeded?: boolean;
  onClose?: (user: NewUser) => void;
}

const AddUser: React.FC<AddUserProps> = ({
  customer,
  setLoadingForPopup,
  isNavigationNeeded = true,
  onClose,
}) => {
  const { customerStore } = useSwageazyStores();
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const c = theme.palette.customColors;
  const location = useLocation();
  const navigate = useNavigate();
  const { showError } = useNotification();

  const queryParams = new URLSearchParams(location.search);
  const userID = queryParams.get("user-id");
  const listingSearch =
    (location.state as { listingSearch?: string } | null)?.listingSearch || "";
  const goToUserListing = () => {
    navigate({ pathname: "/users/listing", search: listingSearch });
  };
  const storedUserId = Number(userStore.userDetails?.id) || 0;
  const userDetailsMatch =
    Boolean(userID) && storedUserId === Number(userID);

  const [isLoading, setIsLoading] = useState(false);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const customerTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Auto-dismiss success message after 3.5 seconds
  useEffect(() => {
    if (!formSuccess) return;
    const timer = setTimeout(() => {
      setFormSuccess(null);
    }, 3500);
    return () => clearTimeout(timer);
  }, [formSuccess]);

  const resolvedUserId = Number(userStore.userDetails?.id ?? userID);
  const isRecoveryResendOnCooldown =
    userStore.isRecoveryResendOnCooldownForUser(resolvedUserId);
  const recoveryResendCountdownLabel =
    userStore.getRecoveryResendCountdownLabel(resolvedUserId);

  const isViewOnly = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_USER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_USER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_USER)
    );
  };

  const isNoAccess = (): boolean => {
    return (
      !userStore.hasPermission(EmployeeRoles.VIEW_USER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_USER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_USER)
    );
  };

  const isCustomerFieldDisabled = () => {
    if (isViewOnly()) {
      return true;
    }
    if (
      userStore.originalUser?.customer_id === undefined ||
      userStore.originalUser?.customer_id === null ||
      userStore.originalUser?.customer_id === -1
    ) {
      return false;
    }
    return Boolean(userID);
  };

  const loadUserActivityLogs = (userId: number, showLoading = true) => {
    if (!userId || Number.isNaN(userId)) {
      return Promise.resolve();
    }
    if (showLoading) {
      setIsActivityLoading(true);
    }
    return userStore
      .getUserActivityLogs(userId)
      .catch((err) => console.error("error in getUserActivityLogs", err))
      .finally(() => {
        if (showLoading) {
          setIsActivityLoading(false);
        }
      });
  };

  useEffect(() => {
    customerStore.searchCustomers({ page_no: 1, limit: 20 }).catch(() => {});
  }, [customerStore]);

  useEffect(() => {
    setErrors({});
    setFormError(null);
    setFormSuccess(null);
    if (!userID) {
      userStore.clearUserDetails();
      userStore.clearActivityLogs();
      return;
    }

    if (Number(userStore.userDetails?.id) === Number(userID)) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setFormError(null);
    userStore
      .getUserDetailsById(Number(userID))
      .then(() => {
        if (cancelled) return;
        const active = userStore.userDetails?.active;
        if (active === 0 || active === false) {
          showError(
            "User Inactive",
            "This user is inactive. Activate the user from the listing to view details.",
          );
          if (isNavigationNeeded) goToUserListing();
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setFormError(
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  useEffect(() => {
    if (!userID) return;
    const userId = Number(userID);
    if (!userId || Number.isNaN(userId)) return;
    loadUserActivityLogs(userId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  useEffect(() => {
    if (!resolvedUserId || Number.isNaN(resolvedUserId)) return;
    userStore.loadRecoveryResendCooldown(resolvedUserId);
  }, [resolvedUserId]);

  useEffect(() => {
    if (!userStore.recoveryResendAvailableAt) return;
    const intervalId = window.setInterval(() => {
      userStore.tickRecoveryResendNow();
    }, 1000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.recoveryResendAvailableAt]);

  useEffect(() => {
    if (customer) {
      userStore.updateCustomerName(customer);
    }
  }, [customer]);

  const customerOptions = useMemo(() => {
    const list = [...customerStore.customers];
    if (
      userStore.userDetails?.customer_id &&
      !list.some((c) => c.id === userStore.userDetails?.customer_id)
    ) {
      list.unshift({
        id: userStore.userDetails.customer_id,
        name:
          userStore.userDetails.customer_name ||
          `Customer #${userStore.userDetails.customer_id}`,
      } as any);
    }
    return list;
  }, [
    customerStore.customers,
    userStore.userDetails?.customer_id,
    userStore.userDetails?.customer_name,
  ]);

  const selectedCustomer = useMemo(() => {
    if (!userStore.userDetails?.customer_id) return null;
    return (
      customerOptions.find(
        (c) => c.id === userStore.userDetails?.customer_id
      ) || {
        id: userStore.userDetails.customer_id,
        name:
          userStore.userDetails.customer_name ||
          `Customer #${userStore.userDetails.customer_id}`,
      }
    );
  }, [
    customerOptions,
    userStore.userDetails?.customer_id,
    userStore.userDetails?.customer_name,
  ]);

  const handleCustomerSearch = (query: string) => {
    if (customerTimerRef.current) {
      clearTimeout(customerTimerRef.current);
    }
    const trimmed = query.trim();
    if (trimmed.length >= 2) {
      customerTimerRef.current = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: trimmed, limit: 20 })
          .catch(() => {});
      }, 350);
    }
  };

  const isValidEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email);
  };

  const getDisplayPhoneNumber = (phone?: string | number) => {
    if (!phone) return "";
    const str = String(phone).trim();
    if (str.startsWith("+91")) {
      return str.slice(3).trim();
    }
    if (str.startsWith("+")) {
      return str.replace(/^\+\d{1,3}/, "").trim();
    }
    return str;
  };

  const handlePhoneNumberChange = (val: string) => {
    const cleaned = val.replace(/[^\d]/g, "");
    if (cleaned.length === 0) {
      userStore.updateIsPhoneNumber("");
    } else {
      userStore.updateIsPhoneNumber(`+91${cleaned}`);
    }
    setFormError(null);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const user = userStore.userDetails;

    if (!user.customer_id || user.customer_id === -1) {
      newErrors.customer_id = "Please select a customer name.";
    }
    if (!user.first_name || user.first_name.trim() === "") {
      newErrors.first_name = "First Name is required.";
    }
    if (!user.email || user.email.trim() === "") {
      newErrors.email = "Email is required.";
    } else if (!isValidEmail(user.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }
    const rawPhone = String(user.phone || "").trim();
    const phoneDigits = rawPhone.replace(/[^\d]/g, "");
    if (!rawPhone || phoneDigits === "" || (rawPhone.startsWith("+91") && phoneDigits === "91")) {
      newErrors.phone = "Phone Number is required.";
    } else if (
      phoneDigits.length < 7 ||
      phoneDigits.length > 15 ||
      !/^\+?\d{7,15}$/.test(rawPhone)
    ) {
      newErrors.phone = "Please enter a valid Phone Number (7-15 digits).";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setFormError(null);
      setFormSuccess(null);
      return false;
    }

    setFormError(null);
    return true;
  };

  const trimInputs = () => {
    userStore.trimTextFields();
  };

  const blockEdgeSpaceKey = (event: React.KeyboardEvent) => {
    const input = inputFromKeyEvent(event);
    if (!input) return;
    if (
      isEdgeSpaceKey(
        event.key,
        input.value,
        input.selectionStart,
        input.selectionEnd,
      )
    ) {
      event.preventDefault();
    }
  };

  const requiredMark = (
    <Box component="span" sx={{ color: colors.error }}>
      *
    </Box>
  );

  const saveDetails = () => {
    trimInputs();
    const rawPhone = String(userStore.userDetails?.phone || "").trim();
    if (rawPhone && !rawPhone.startsWith("+")) {
      const digits = rawPhone.replace(/[^\d]/g, "");
      userStore.updateIsPhoneNumber(`+91${digits}`);
    }
    if (!validateForm()) return;
    if (userID) {
      updateUser();
    } else {
      createUser();
    }
  };

  const createUser = () => {
    setIsLoading(true);
    setFormError(null);
    setFormSuccess(null);
    userStore
      .createUser()
      .then((res) => {
        if (setLoadingForPopup) {
          if (onClose) {
            onClose(res.result);
          }
          userStore.resetUser();
        } else {
          const newId = Number(res?.result?.id);
          if (newId) {
            navigate(
              {
                pathname: "/users/add-edit-user",
                search: `?user-id=${newId}`,
              },
              { state: location.state },
            );
          }
        }
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          "Something went wrong! Please try again later.";
        const lower = msg.toLowerCase();
        if (lower.includes("email")) {
          setErrors((prev) => ({ ...prev, email: msg }));
        } else if (lower.includes("phone")) {
          setErrors((prev) => ({ ...prev, phone: msg }));
        } else if (lower.includes("customer")) {
          setErrors((prev) => ({ ...prev, customer_id: msg }));
        } else if (lower.includes("first name")) {
          setErrors((prev) => ({ ...prev, first_name: msg }));
        } else {
          setFormError(msg);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const updateUser = () => {
    setIsLoading(true);
    setFormError(null);
    setFormSuccess(null);
    userStore
      .updateUserDetails()
      .then((res) => {
        if (setLoadingForPopup) {
          if (onClose) {
            onClose(res?.result);
          }
        } else {
          loadUserActivityLogs(resolvedUserId, false);
        }
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          "Something went wrong! Please try again later.";
        const lower = msg.toLowerCase();
        if (lower.includes("email")) {
          setErrors((prev) => ({ ...prev, email: msg }));
        } else if (lower.includes("phone")) {
          setErrors((prev) => ({ ...prev, phone: msg }));
        } else if (lower.includes("customer")) {
          setErrors((prev) => ({ ...prev, customer_id: msg }));
        } else if (lower.includes("first name")) {
          setErrors((prev) => ({ ...prev, first_name: msg }));
        } else {
          setFormError(msg);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const resetForm = () => {
    userStore.discardChanges();
    setErrors({});
    setFormError(null);
    setFormSuccess(null);
  };

  const handleSendRecoveryEmail = () => {
    if (!resolvedUserId || Number.isNaN(resolvedUserId)) {
      setFormError("Unable to send email: user id is missing.");
      return;
    }

    if (isRecoveryResendOnCooldown) return;

    setFormError(null);
    setFormSuccess(null);

    userStore
      .sendCustomerPasswordRecoveryLink(resolvedUserId)
      .then(({ payload }) => {
        const linkExpiresInHours = payload.linkExpiresInHours ?? 12;
        setFormSuccess(
          `The Password Reset Link has been sent successfully. It will remain valid for ${linkExpiresInHours} hours.`
        );
        loadUserActivityLogs(resolvedUserId, false);
      })
      .catch((err) => {
        setFormError(
          err?.response?.data?.message ||
            "Failed to send password reset email. Please try again later."
        );
      });
  };

  const handleOpenActivityDrawer = () => {
    if (!resolvedUserId || Number.isNaN(resolvedUserId)) {
      setFormError("Unable to load activity history: user id is missing.");
      return;
    }
    setIsActivityDrawerOpen(true);
    loadUserActivityLogs(resolvedUserId);
  };

  const handleBackNavigation = () => {
    if (isNavigationNeeded) {
      goToUserListing();
    } else if (onClose) {
      onClose(userStore.userDetails as any);
    }
  };

  const errorBorder = {
    borderColor: `${colors.error} !important`,
    borderWidth: "1.5px !important",
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "0.875rem",
      backgroundColor: colors.surface,
      color: colors.title,
      "& input": {
        color: colors.title,
      },
      "& fieldset, & .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border,
      },
      "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.accentPurple,
      },
      "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.accentPurple,
        borderWidth: 1.5,
      },
      "&.Mui-error fieldset, &.Mui-error .MuiOutlinedInput-notchedOutline":
        errorBorder,
      "&.Mui-error:hover fieldset, &.Mui-error:hover .MuiOutlinedInput-notchedOutline":
        errorBorder,
      "&.Mui-error.Mui-focused fieldset, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline":
        errorBorder,
      "&.Mui-disabled": {
        backgroundColor: c.lightGrayBackground,
        "& fieldset, & .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.border,
        },
      },
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.75rem",
      mt: 0.5,
      mx: 0.5,
      fontWeight: 500,
      color: `${colors.error} !important`,
    },
  };

  const selectStyles = {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: colors.surface,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accentPurple,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accentPurple,
      borderWidth: 1.5,
    },
    "&.Mui-disabled": {
      backgroundColor: c.lightGrayBackground,
    },
  };

  const labelProps = {
    variant: "heading8",
    color: c.navyText,
    sx: { mb: 0.75 },
  } as const;

  if (userID && !userDetailsMatch) {
    return (
      <Box
        sx={{
          backgroundColor: colors.pageBg,
          minHeight: "40vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          py: 6,
        }}
      >
        {formError ? (
          <>
            <CustomTypography variant="body2" color={c.errorRed}>
              {formError}
            </CustomTypography>
            {isNavigationNeeded ? (
              <AppButton
                label="Back to users"
                variant="transparentPrimary"
                onClick={goToUserListing}
              />
            ) : null}
          </>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: colors.pageBg, minHeight: "100vh", py: 3 }}>
      <Backdrop
        sx={{
          color: c.white,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="xl">
        {/* Header Bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Back Button, Title & Subtitle */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={handleBackNavigation}
              sx={{
                mt: 0.5,
                p: 0.75,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.surface,
                color: c.mutedSlateText,
                "&:hover": {
                  backgroundColor: c.lightGrayBackground,
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Box>
              <CustomTypography
                variant="heading3"
                color={colors.title}
                style={{ fontSize: undefined }}
                sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
              >
                {isViewOnly()
                  ? "View User"
                  : userID
                    ? "Update User"
                    : "Create User"}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={colors.subtitle}
                style={{ marginTop: 2 }}
              >
                {userID
                  ? "Modify User records, and send email to reset password."
                  : "Add user records and send email to set password."}
              </CustomTypography>
            </Box>
          </Box>

          {/* Activity log Button (Update / View mode only) */}
          {Boolean(userID) && (
            <AppButton
              label="Activity log"
              variant="grayscale"
              onClick={handleOpenActivityDrawer}
            />
          )}
        </Box>

        {/* Permission Denied View */}
        {isNoAccess() ? (
          <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
            <CustomTypography
              variant="heading6"
              fontWeight={500}
              color={colors.subtitle}
            >
              You do not have permission to view this section.
            </CustomTypography>
          </Paper>
        ) : (
          <Box>
            {/* User Form Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 4 },
                mb: 3,
                borderRadius: 4,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                boxShadow: `0 1px 3px ${c.shadowVeryLight}`,
              }}
            >
              <Grid2 container spacing={3}>
                {/* Row 1: Select Customer Name & Is it Admin ? */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <CustomTypography {...labelProps}>
                    Select Customer Name {requiredMark}
                  </CustomTypography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disabled={isCustomerFieldDisabled()}
                    options={customerOptions}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return (
                        option?.name ||
                        (option?.id ? `Customer #${option.id}` : "")
                      );
                    }}
                    isOptionEqualToValue={(option, val) =>
                      Boolean(val && option && option.id === val.id)
                    }
                    value={selectedCustomer}
                    onChange={(_, newValue) => {
                      if (newValue && typeof newValue !== "string") {
                        userStore.updateCustomerName([
                          {
                            id: String(newValue.id),
                            value: newValue.name || "",
                          },
                        ]);
                      } else {
                        userStore.updateCustomerName(undefined);
                      }
                      if (errors.customer_id) {
                        setErrors((prev) => ({ ...prev, customer_id: "" }));
                      }
                      setFormError(null);
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input") {
                        handleCustomerSearch(newInputValue);
                      } else if (reason === "clear") {
                        userStore.updateCustomerName(undefined);
                      }
                    }}
                    popupIcon={
                      <KeyboardArrowDownIcon sx={{ color: colors.subtitle }} />
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          selectedCustomer ? undefined : "Select Customer Name"
                        }
                        error={Boolean(errors.customer_id)}
                        helperText={errors.customer_id}
                        sx={inputStyles}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <CustomTypography {...labelProps}>Is it Admin ?</CustomTypography>
                  <Select
                    fullWidth
                    size="small"
                    value={userStore.userDetails?.is_admin ? "1" : "0"}
                    onChange={(e) => {
                      userStore.updateIsAdmin(e.target.value === "1");
                      setFormError(null);
                    }}
                    disabled={isViewOnly()}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyles}
                  >
                    <MenuItem value="1">Yes</MenuItem>
                    <MenuItem value="0">No</MenuItem>
                  </Select>
                </Grid2>

                {/* Row 2: First Name & Last Name */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <CustomTypography {...labelProps}>
                    First Name {requiredMark}
                  </CustomTypography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add First Name"
                    value={userStore.userDetails?.first_name || ""}
                    onChange={(e) => {
                      userStore.updateFirstName(blockEdgeSpaces(e.target.value));
                      if (errors.first_name) {
                        setErrors((prev) => ({ ...prev, first_name: "" }));
                      }
                    }}
                    onKeyDown={blockEdgeSpaceKey}
                    onBlur={trimInputs}
                    error={Boolean(errors.first_name)}
                    helperText={errors.first_name}
                    disabled={isViewOnly()}
                    sx={inputStyles}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <CustomTypography {...labelProps}>Last Name</CustomTypography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add Name"
                    value={userStore.userDetails?.last_name || ""}
                    onChange={(e) => {
                      userStore.updateLastName(blockEdgeSpaces(e.target.value));
                      setFormError(null);
                    }}
                    onKeyDown={blockEdgeSpaceKey}
                    onBlur={trimInputs}
                    disabled={isViewOnly()}
                    sx={inputStyles}
                  />
                </Grid2>

                {/* Row 3: Email & Phone Number */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <CustomTypography {...labelProps}>
                    Email {requiredMark}
                  </CustomTypography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add Email"
                    type="email"
                    value={userStore.userDetails?.email || ""}
                    onChange={(e) => {
                      userStore.updateEmail(blockEdgeSpaces(e.target.value));
                      if (errors.email) {
                        setErrors((prev) => ({ ...prev, email: "" }));
                      }
                    }}
                    onKeyDown={blockEdgeSpaceKey}
                    onBlur={trimInputs}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    disabled={Boolean(userID) || isViewOnly()}
                    sx={inputStyles}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <CustomTypography {...labelProps}>
                    Phone Number {requiredMark}
                  </CustomTypography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add Number"
                    type="tel"
                    value={getDisplayPhoneNumber(userStore.userDetails?.phone)}
                    onChange={(e) => {
                      handlePhoneNumberChange(e.target.value);
                      if (errors.phone) {
                        setErrors((prev) => ({ ...prev, phone: "" }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === " ") e.preventDefault();
                    }}
                    onBlur={trimInputs}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    disabled={isViewOnly()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: c.navyText,
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              cursor: "default",
                              borderRight: `1px solid ${colors.border}`,
                              pr: 1.25,
                              py: 0.5,
                            }}
                          >
                            <KeyboardArrowDownIcon
                              sx={{ fontSize: 16, color: colors.subtitle }}
                            />
                            <span>+91</span>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputStyles}
                  />
                </Grid2>

                {/* Row 4: Send Email Button (Update mode only) */}
                {Boolean(userID) && !isViewOnly() && (
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mt: 1 }}>
                      <AppButton
                        label={
                          userStore.isSendingRecoveryEmail
                            ? "Sending..."
                            : "Send Email"
                        }
                        variant="primary"
                        startIcon={<EmailOutlinedIcon />}
                        onClick={handleSendRecoveryEmail}
                        disabled={
                          userStore.isSendingRecoveryEmail ||
                          isLoading ||
                          isRecoveryResendOnCooldown
                        }
                      />
                      <CustomTypography
                        variant="caption"
                        color={colors.subtitle}
                        style={{ display: "block", marginTop: 8 }}
                      >
                        {isRecoveryResendOnCooldown
                          ? `Resend available in ${recoveryResendCountdownLabel}`
                          : "Sends reset password link to the user’s email."}
                      </CustomTypography>
                    </Box>
                  </Grid2>
                )}
              </Grid2>
            </Paper>

            {/* Client alert settings (saved client users only; saves on its own) */}
            {userID &&
              userDetailsMatch &&
              Number(userStore.originalUser?.customer_id) > 0 && (
                <UserAlertSettingsCard
                  userId={Number(userID)}
                  disabled={isViewOnly()}
                />
              )}

            {/* Inline Error Message Banner */}
            {formError && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: `${colors.error}14`,
                    border: `1px solid ${colors.error}40`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    maxWidth: 600,
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ErrorOutlineIcon
                      sx={{ fontSize: 18, color: colors.error }}
                    />
                    <CustomTypography
                      variant="body2"
                      color={colors.error}
                      fontWeight={500}
                    >
                      {formError}
                    </CustomTypography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setFormError(null)}
                    sx={{ color: colors.error, p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Inline Simple Success Message */}
            {formSuccess && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  mb: 0.5,
                  textAlign: "center",
                }}
              >
                <CustomTypography
                  variant="body2"
                  color={colors.success}
                  fontWeight={500}
                >
                  {formSuccess}
                </CustomTypography>
              </Box>
            )}

            {/* Bottom Action Buttons: Centered Reset and Save */}
            {!isViewOnly() && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2.5,
                  mt: 4,
                  mb: 8,
                }}
              >
                <AppButton
                  label="Reset"
                  variant="grayscale"
                  onClick={resetForm}
                  disabled={isLoading}
                  customStyles={{ minWidth: 120, height: 42 }}
                />
                <AppButton
                  label={userID ? "Save" : "Create"}
                  variant="primary"
                  onClick={saveDetails}
                  disabled={isLoading}
                  customStyles={{ minWidth: 120, height: 42 }}
                />
              </Box>
            )}
          </Box>
        )}

        <ActivityLogDrawer
          isOpen={isActivityDrawerOpen}
          onClose={() => setIsActivityDrawerOpen(false)}
          activityLogs={userStore.activityLogs}
          isLoading={isActivityLoading}
          title="Activity History"
          subtitle={userID ? `User #${userID}` : null}
        />
      </Container>
    </Box>
  );
};

export default observer(AddUser);
